import * as ss from "simple-statistics";

export interface ChartDataPoint {
  name: string;
  value: number;
  percentage?: number;
  color?: string;
}

export interface ComparisonDataPoint {
  variant: string;
  [key: string]: string | number;
}

export interface OutcomeComparisonData {
  outcome: string;
  data: ComparisonDataPoint[];
}

export const COLORS = ["#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#06B6D4", "#84CC16", "#F97316"];

export interface VariantRanking {
  variant: string;
  rank: number;
  value: number;
  confidence: number;
  significance: string;
  isSignificantlyBetter: boolean;
  isSignificantlyWorse: boolean;
  comparisons: Array<{
    vsVariant: string;
    confidence: number;
    isBetter: boolean;
  }>;
}

export type TargetType = "minimize" | "maximize" | "exact";

export interface TargetConfig {
  type: TargetType;
  value?: number; // Only used when type is 'exact'
}

export const rankVariants = (data: ComparisonDataPoint[], metric: string, targetConfig?: TargetConfig): VariantRanking[] => {
  if (data.length < 2) return [];

  // For string outcomes, we need to group by string value and rank variants for each value
  if (data[0].stringValue !== undefined) {
    // Group data by string value
    const groupedByValue = data.reduce(
      (acc, item) => {
        const stringValue = item.stringValue as string;
        if (!acc[stringValue]) {
          acc[stringValue] = [];
        }
        acc[stringValue].push(item);
        return acc;
      },
      {} as Record<string, ComparisonDataPoint[]>,
    );

    // For now, return ranking for the first string value (most common)
    const firstStringValue = Object.keys(groupedByValue)[0];
    if (!firstStringValue) return [];

    const valueData = groupedByValue[firstStringValue];
    return rankVariantsForValue(valueData, "rateValue", targetConfig);
  } else {
    // For non-string outcomes, rank based on the metric
    return rankVariantsForValue(data, metric, targetConfig);
  }
};

const rankVariantsForValue = (data: ComparisonDataPoint[], metric: string, targetConfig?: TargetConfig): VariantRanking[] => {
  if (data.length < 2) return [];

  // Determine sorting direction based on target configuration
  let isHigherBetter: boolean;
  let sortedData: ComparisonDataPoint[];

  if (targetConfig) {
    switch (targetConfig.type) {
      case "maximize":
        isHigherBetter = true;
        sortedData = [...data].sort((a, b) => {
          const valueA = a[metric] as number;
          const valueB = b[metric] as number;
          return valueB - valueA; // Higher is better
        });
        break;
      case "minimize":
        isHigherBetter = false;
        sortedData = [...data].sort((a, b) => {
          const valueA = a[metric] as number;
          const valueB = b[metric] as number;
          return valueA - valueB; // Lower is better
        });
        break;
      case "exact":
        // For exact targets, sort by distance from target (closest first)
        const targetValue = targetConfig.value || 0;
        sortedData = [...data].sort((a, b) => {
          const valueA = a[metric] as number;
          const valueB = b[metric] as number;
          const distanceA = Math.abs(valueA - targetValue);
          const distanceB = Math.abs(valueB - targetValue);
          return distanceA - distanceB;
        });
        // For exact targets, we'll use a custom comparison logic
        return buildRankingsForExactTarget(sortedData, metric, targetValue);
      default:
        isHigherBetter = metric === "successRateValue" || metric === "rateValue";
        sortedData = [...data].sort((a, b) => {
          const valueA = a[metric] as number;
          const valueB = b[metric] as number;
          return isHigherBetter ? valueB - valueA : valueA - valueB;
        });
    }
  } else {
    // Default behavior for string outcomes or when no target is specified
    isHigherBetter = metric === "successRateValue" || metric === "rateValue";
    sortedData = [...data].sort((a, b) => {
      const valueA = a[metric] as number;
      const valueB = b[metric] as number;
      return isHigherBetter ? valueB - valueA : valueA - valueB;
    });
  }

  return buildRankingsFromSortedData(sortedData, metric, isHigherBetter);
};

const buildRankingsForExactTarget = (sortedData: ComparisonDataPoint[], metric: string, targetValue: number): VariantRanking[] => {
  const rankings: VariantRanking[] = [];

  for (let i = 0; i < sortedData.length; i++) {
    const variant = sortedData[i];
    const variantValue = variant[metric] as number;
    const variantCount = variant.count as number;

    const comparisons: Array<{ vsVariant: string; confidence: number; isBetter: boolean }> = [];
    let maxConfidence = 0;
    let isSignificantlyBetter = false;
    let isSignificantlyWorse = false;

    // Compare against all other variants
    for (let j = 0; j < sortedData.length; j++) {
      if (i === j) continue;

      const otherVariant = sortedData[j];
      const otherValue = otherVariant[metric] as number;
      const otherCount = otherVariant.count as number;

      if (isNaN(variantValue) || isNaN(otherValue) || variantCount === 0 || otherCount === 0) {
        continue;
      }

      try {
        // Use t-test approximation for numeric values
        const pooledStdDev = Math.sqrt((Math.pow(variantValue, 2) + Math.pow(otherValue, 2)) / 2);
        const standardError = pooledStdDev * Math.sqrt(1 / variantCount + 1 / otherCount);
        const zScore = Math.abs(otherValue - variantValue) / standardError;

        const pValue = (2 * Math.exp((-zScore * zScore) / 2)) / Math.sqrt(2 * Math.PI);
        const confidence = Math.max(0, (1 - pValue) * 100);

        // For exact targets, "better" means closer to target
        const distanceToTarget = Math.abs(variantValue - targetValue);
        const otherDistanceToTarget = Math.abs(otherValue - targetValue);
        const isBetter = distanceToTarget < otherDistanceToTarget;

        comparisons.push({ vsVariant: otherVariant.variant, confidence, isBetter });

        if (confidence > maxConfidence) {
          maxConfidence = confidence;
        }

        if (confidence >= 95 && isBetter) {
          isSignificantlyBetter = true;
        } else if (confidence >= 95 && !isBetter) {
          isSignificantlyWorse = true;
        }
      } catch (error) {
        // Skip this comparison if it fails
      }
    }

    // Determine significance text
    let significance = "Not Significant";
    if (maxConfidence >= 99) significance = `${maxConfidence.toFixed(1)}% Confidence`;
    else if (maxConfidence >= 95) significance = `${maxConfidence.toFixed(1)}% Confidence`;
    else if (maxConfidence >= 90) significance = `${maxConfidence.toFixed(1)}% Confidence`;
    else if (maxConfidence >= 80) significance = `${maxConfidence.toFixed(1)}% Confidence`;
    else if (maxConfidence >= 70) significance = `${maxConfidence.toFixed(1)}% Confidence`;
    else if (maxConfidence >= 60) significance = `${maxConfidence.toFixed(1)}% Confidence`;
    else if (maxConfidence >= 50) significance = `${maxConfidence.toFixed(1)}% Confidence`;
    else significance = `${maxConfidence.toFixed(1)}% Confidence`;

    rankings.push({
      variant: variant.variant,
      rank: i + 1,
      value: variantValue,
      confidence: maxConfidence,
      significance,
      isSignificantlyBetter,
      isSignificantlyWorse,
      comparisons,
    });
  }

  return rankings;
};

const buildRankingsFromSortedData = (sortedData: ComparisonDataPoint[], metric: string, isHigherBetter: boolean): VariantRanking[] => {
  const rankings: VariantRanking[] = [];

  // Calculate pairwise comparisons and build rankings
  for (let i = 0; i < sortedData.length; i++) {
    const variant = sortedData[i];
    const variantValue = variant[metric] as number;
    const variantCount = variant.count as number;

    const comparisons: Array<{ vsVariant: string; confidence: number; isBetter: boolean }> = [];
    let maxConfidence = 0;
    let isSignificantlyBetter = false;
    let isSignificantlyWorse = false;

    // Compare against all other variants
    for (let j = 0; j < sortedData.length; j++) {
      if (i === j) continue;

      const otherVariant = sortedData[j];
      const otherValue = otherVariant[metric] as number;
      const otherCount = otherVariant.count as number;

      if (isNaN(variantValue) || isNaN(otherValue) || variantCount === 0 || otherCount === 0) {
        continue;
      }

      try {
        let confidence = 0;

        if (metric === "successRateValue" || metric === "rateValue") {
          // Use chi-square test for proportions
          const success1 = Math.round((variantValue / 100) * variantCount);
          const failure1 = variantCount - success1;
          const success2 = Math.round((otherValue / 100) * otherCount);
          const failure2 = otherCount - success2;

          const total = success1 + failure1 + success2 + failure2;
          const expectedSuccess1 = ((success1 + failure1) * (success1 + success2)) / total;
          const expectedFailure1 = ((success1 + failure1) * (failure1 + failure2)) / total;
          const expectedSuccess2 = ((success2 + failure2) * (success1 + success2)) / total;
          const expectedFailure2 = ((success2 + failure2) * (failure1 + failure2)) / total;

          const chiSquare =
            Math.pow(success1 - expectedSuccess1, 2) / expectedSuccess1 +
            Math.pow(failure1 - expectedFailure1, 2) / expectedFailure1 +
            Math.pow(success2 - expectedSuccess2, 2) / expectedSuccess2 +
            Math.pow(failure2 - expectedFailure2, 2) / expectedFailure2;

          const zScore = Math.sqrt(chiSquare);
          const pValue = (2 * Math.exp((-zScore * zScore) / 2)) / Math.sqrt(2 * Math.PI);
          confidence = Math.max(0, (1 - pValue) * 100);
        } else {
          // Use t-test approximation for numeric values
          const pooledStdDev = Math.sqrt((Math.pow(variantValue, 2) + Math.pow(otherValue, 2)) / 2);
          const standardError = pooledStdDev * Math.sqrt(1 / variantCount + 1 / otherCount);
          const zScore = Math.abs(otherValue - variantValue) / standardError;

          const pValue = (2 * Math.exp((-zScore * zScore) / 2)) / Math.sqrt(2 * Math.PI);
          confidence = Math.max(0, (1 - pValue) * 100);
        }

        const isBetter = isHigherBetter ? variantValue > otherValue : variantValue < otherValue;
        comparisons.push({ vsVariant: otherVariant.variant, confidence, isBetter });

        if (confidence > maxConfidence) {
          maxConfidence = confidence;
        }

        if (confidence >= 95 && isBetter) {
          isSignificantlyBetter = true;
        } else if (confidence >= 95 && !isBetter) {
          isSignificantlyWorse = true;
        }
      } catch (error) {
        // Skip this comparison if it fails
      }
    }

    // Determine significance text
    let significance = "Not Significant";
    if (maxConfidence >= 99) significance = `${maxConfidence.toFixed(1)}% Confidence`;
    else if (maxConfidence >= 95) significance = `${maxConfidence.toFixed(1)}% Confidence`;
    else if (maxConfidence >= 90) significance = `${maxConfidence.toFixed(1)}% Confidence`;
    else if (maxConfidence >= 80) significance = `${maxConfidence.toFixed(1)}% Confidence`;
    else if (maxConfidence >= 70) significance = `${maxConfidence.toFixed(1)}% Confidence`;
    else if (maxConfidence >= 60) significance = `${maxConfidence.toFixed(1)}% Confidence`;
    else if (maxConfidence >= 50) significance = `${maxConfidence.toFixed(1)}% Confidence`;
    else significance = `${maxConfidence.toFixed(1)}% Confidence`;

    rankings.push({
      variant: variant.variant,
      rank: i + 1,
      value: variantValue,
      confidence: maxConfidence,
      significance,
      isSignificantlyBetter,
      isSignificantlyWorse,
      comparisons,
    });
  }

  return rankings;
};

export const processVariantDataForPieChart = (optionCounts: Record<string, { count: number }>) => {
  return Object.entries(optionCounts).map(([variant, option], index) => ({
    name: variant,
    value: option.count,
    color: COLORS[index % COLORS.length],
  }));
};

export const processStringOutcomeData = (
  optionCounts: Record<string, { count: number; outcomes: Record<string, { counts: Record<string, number> }> }>,
  outcomeName: string,
): ComparisonDataPoint[] => {
  // Get all unique string values across all variants
  const allStringValues = new Set<string>();
  Object.values(optionCounts).forEach((option) => {
    const outcomeData = option.outcomes[outcomeName];
    if (outcomeData) {
      Object.keys(outcomeData.counts).forEach((value) => allStringValues.add(value));
    }
  });

  // Create data points for each variant-value combination
  const result: ComparisonDataPoint[] = [];

  Object.entries(optionCounts).forEach(([variant, option]) => {
    const outcomeData = option.outcomes[outcomeName];
    const totalOutcomes = outcomeData ? Object.values(outcomeData.counts).reduce((sum, count) => sum + count, 0) : 0;

    // Create a data point for each unique string value
    allStringValues.forEach((stringValue) => {
      const count = outcomeData?.counts[stringValue] || 0;
      const rate = totalOutcomes > 0 ? (count / totalOutcomes) * 100 : 0;

      const dataPoint = {
        variant,
        stringValue,
        count,
        totalOutcomes,
        rate: rate.toFixed(1),
        rateValue: rate,
      } as ComparisonDataPoint;

      result.push(dataPoint);
    });
  });

  return result;
};

export const processBooleanOutcomeData = (
  optionCounts: Record<string, { count: number; outcomes: Record<string, { counts: Record<string, number> }> }>,
  outcomeName: string,
): ComparisonDataPoint[] => {
  return Object.entries(optionCounts).map(([variant, option]) => {
    const outcomeData = option.outcomes[outcomeName];
    const trueCount = outcomeData?.counts["true"] || 0;
    const falseCount = outcomeData?.counts["false"] || 0;
    const totalOutcomes = trueCount + falseCount;
    const successRate = totalOutcomes > 0 ? (trueCount / totalOutcomes) * 100 : 0;

    return {
      variant,
      count: option.count,
      trueCount,
      falseCount,
      totalOutcomes,
      successRate: successRate.toFixed(1),
      successRateValue: successRate,
    };
  });
};

export const processIntegerOutcomeData = (
  optionCounts: Record<string, { count: number; outcomes: Record<string, { counts: Record<string, number> }> }>,
  outcomeName: string,
): ComparisonDataPoint[] => {
  return Object.entries(optionCounts).map(([variant, option]) => {
    const outcomeData = option.outcomes[outcomeName];
    if (!outcomeData) {
      return {
        variant,
        count: option.count,
        average: 0,
        totalOutcomes: 0,
      } as ComparisonDataPoint;
    }

    const entries = Object.entries(outcomeData.counts);
    const totalOutcomes = entries.reduce((sum, [_, count]) => sum + count, 0);
    const weightedSum = entries.reduce((sum, [value, count]) => sum + (parseFloat(value) || 0) * count, 0);
    const average = totalOutcomes > 0 ? weightedSum / totalOutcomes : 0;

    return {
      variant,
      count: option.count,
      average: average.toFixed(2),
      averageValue: average,
      totalOutcomes,
    } as ComparisonDataPoint;
  });
};

export const processFloatOutcomeData = (
  optionCounts: Record<string, { count: number; outcomes: Record<string, { counts: Record<string, number> }> }>,
  outcomeName: string,
): ComparisonDataPoint[] => {
  return Object.entries(optionCounts).map(([variant, option]) => {
    const outcomeData = option.outcomes[outcomeName];
    if (!outcomeData) {
      return {
        variant,
        count: option.count,
        average: 0,
        totalOutcomes: 0,
      } as ComparisonDataPoint;
    }

    const entries = Object.entries(outcomeData.counts);
    const totalOutcomes = entries.reduce((sum, [_, count]) => sum + count, 0);
    const weightedSum = entries.reduce((sum, [value, count]) => sum + (parseFloat(value) || 0) * count, 0);
    const average = totalOutcomes > 0 ? weightedSum / totalOutcomes : 0;

    return {
      variant,
      count: option.count,
      average: average.toFixed(2),
      averageValue: average,
      totalOutcomes,
    } as ComparisonDataPoint;
  });
};

export const calculateStatisticalSignificance = (
  data: ComparisonDataPoint[],
  metric: string,
): Record<string, { significance: string; confidence: number; pValue: number }> => {
  const results: Record<string, { significance: string; confidence: number; pValue: number }> = {};

  if (data.length < 2) return results;

  // For string outcomes, we need to group by string value and compare all variants for each value
  if (data[0].stringValue !== undefined) {
    // Group data by string value
    const groupedByValue = data.reduce(
      (acc, item) => {
        const stringValue = item.stringValue as string;
        if (!acc[stringValue]) {
          acc[stringValue] = [];
        }
        acc[stringValue].push(item);
        return acc;
      },
      {} as Record<string, ComparisonDataPoint[]>,
    );

    // For each string value, perform pairwise comparisons between all variants
    Object.entries(groupedByValue).forEach(([stringValue, valueData]) => {
      if (valueData.length < 2) return;

      // Perform all pairwise comparisons
      for (let i = 0; i < valueData.length; i++) {
        for (let j = i + 1; j < valueData.length; j++) {
          const variant1 = valueData[i];
          const variant2 = valueData[j];
          const value1 = variant1.rateValue as number;
          const value2 = variant2.rateValue as number;
          const count1 = variant1.totalOutcomes as number;
          const count2 = variant2.totalOutcomes as number;

          // Skip if we don't have valid data
          if (isNaN(value1) || isNaN(value2) || count1 === 0 || count2 === 0) {
            continue;
          }

          try {
            // Use chi-square test for proportions (2x2 contingency table)
            const success1 = Math.round((value1 / 100) * count1);
            const failure1 = count1 - success1;
            const success2 = Math.round((value2 / 100) * count2);
            const failure2 = count2 - success2;

            // Calculate chi-square statistic
            const total = success1 + failure1 + success2 + failure2;
            const expectedSuccess1 = ((success1 + failure1) * (success1 + success2)) / total;
            const expectedFailure1 = ((success1 + failure1) * (failure1 + failure2)) / total;
            const expectedSuccess2 = ((success2 + failure2) * (success1 + success2)) / total;
            const expectedFailure2 = ((success2 + failure2) * (failure1 + failure2)) / total;

            const chiSquare =
              Math.pow(success1 - expectedSuccess1, 2) / expectedSuccess1 +
              Math.pow(failure1 - expectedFailure1, 2) / expectedFailure1 +
              Math.pow(success2 - expectedSuccess2, 2) / expectedSuccess2 +
              Math.pow(failure2 - expectedFailure2, 2) / expectedFailure2;

            const zScore = Math.sqrt(chiSquare);
            const pValue = (2 * Math.exp((-zScore * zScore) / 2)) / Math.sqrt(2 * Math.PI);
            const confidence = Math.max(0, (1 - pValue) * 100);

            let significance = "Not Significant";
            if (confidence >= 99) significance = `${confidence.toFixed(1)}% Confidence`;
            else if (confidence >= 95) significance = `${confidence.toFixed(1)}% Confidence`;
            else if (confidence >= 90) significance = `${confidence.toFixed(1)}% Confidence`;
            else if (confidence >= 80) significance = `${confidence.toFixed(1)}% Confidence`;
            else if (confidence >= 70) significance = `${confidence.toFixed(1)}% Confidence`;
            else if (confidence >= 60) significance = `${confidence.toFixed(1)}% Confidence`;
            else if (confidence >= 50) significance = `${confidence.toFixed(1)}% Confidence`;
            else significance = `${confidence.toFixed(1)}% Confidence`;

            // Store results for both variants
            const key1 = `${variant1.variant}-${stringValue}`;
            const key2 = `${variant2.variant}-${stringValue}`;

            // Only store the better result if we already have one
            if (!results[key1] || results[key1].confidence < confidence) {
              results[key1] = {
                significance: `${significance} vs ${variant2.variant}`,
                confidence,
                pValue,
              };
            }
            if (!results[key2] || results[key2].confidence < confidence) {
              results[key2] = {
                significance: `${significance} vs ${variant1.variant}`,
                confidence,
                pValue,
              };
            }
          } catch (error) {
            // Fallback to z-test
            const se1 = Math.sqrt((value1 * (100 - value1)) / count1);
            const se2 = Math.sqrt((value2 * (100 - value2)) / count2);
            const pooledSE = Math.sqrt(se1 * se1 + se2 * se2);

            if (pooledSE > 0 && !isNaN(pooledSE)) {
              const zScore = Math.abs(value2 - value1) / pooledSE;
              const pValue = (2 * Math.exp((-zScore * zScore) / 2)) / Math.sqrt(2 * Math.PI);
              const confidence = Math.max(0, (1 - pValue) * 100);

              let significance = "Not Significant";
              if (confidence >= 99) significance = `${confidence.toFixed(1)}% Confidence`;
              else if (confidence >= 95) significance = `${confidence.toFixed(1)}% Confidence`;
              else if (confidence >= 90) significance = `${confidence.toFixed(1)}% Confidence`;
              else if (confidence >= 80) significance = `${confidence.toFixed(1)}% Confidence`;
              else if (confidence >= 70) significance = `${confidence.toFixed(1)}% Confidence`;
              else if (confidence >= 60) significance = `${confidence.toFixed(1)}% Confidence`;
              else if (confidence >= 50) significance = `${confidence.toFixed(1)}% Confidence`;
              else significance = `${confidence.toFixed(1)}% Confidence`;

              const key1 = `${variant1.variant}-${stringValue}`;
              const key2 = `${variant2.variant}-${stringValue}`;

              if (!results[key1] || results[key1].confidence < confidence) {
                results[key1] = {
                  significance: `${significance} vs ${variant2.variant}`,
                  confidence,
                  pValue,
                };
              }
              if (!results[key2] || results[key2].confidence < confidence) {
                results[key2] = {
                  significance: `${significance} vs ${variant1.variant}`,
                  confidence,
                  pValue,
                };
              }
            }
          }
        }
      }
    });
  } else {
    // For non-string outcomes, perform pairwise comparisons between all variants
    for (let i = 0; i < data.length; i++) {
      for (let j = i + 1; j < data.length; j++) {
        const variant1 = data[i];
        const variant2 = data[j];
        const value1 = variant1[metric] as number;
        const value2 = variant2[metric] as number;
        const count1 = variant1.count as number;
        const count2 = variant2.count as number;

        // Skip if we don't have valid data
        if (isNaN(value1) || isNaN(value2) || count1 === 0 || count2 === 0) {
          continue;
        }

        try {
          if (metric === "successRateValue") {
            // Boolean outcomes - use chi-square test for proportions
            const success1 = Math.round((value1 / 100) * count1);
            const failure1 = count1 - success1;
            const success2 = Math.round((value2 / 100) * count2);
            const failure2 = count2 - success2;

            const total = success1 + failure1 + success2 + failure2;
            const expectedSuccess1 = ((success1 + failure1) * (success1 + success2)) / total;
            const expectedFailure1 = ((success1 + failure1) * (failure1 + failure2)) / total;
            const expectedSuccess2 = ((success2 + failure2) * (success1 + success2)) / total;
            const expectedFailure2 = ((success2 + failure2) * (failure1 + failure2)) / total;

            const chiSquare =
              Math.pow(success1 - expectedSuccess1, 2) / expectedSuccess1 +
              Math.pow(failure1 - expectedFailure1, 2) / expectedFailure1 +
              Math.pow(success2 - expectedSuccess2, 2) / expectedSuccess2 +
              Math.pow(failure2 - expectedFailure2, 2) / expectedFailure2;

            const zScore = Math.sqrt(chiSquare);
            const pValue = (2 * Math.exp((-zScore * zScore) / 2)) / Math.sqrt(2 * Math.PI);
            const confidence = Math.max(0, (1 - pValue) * 100);

            let significance = "Not Significant";
            if (confidence >= 99) significance = `${confidence.toFixed(1)}% Confidence`;
            else if (confidence >= 95) significance = `${confidence.toFixed(1)}% Confidence`;
            else if (confidence >= 90) significance = `${confidence.toFixed(1)}% Confidence`;
            else if (confidence >= 80) significance = `${confidence.toFixed(1)}% Confidence`;
            else if (confidence >= 70) significance = `${confidence.toFixed(1)}% Confidence`;
            else if (confidence >= 60) significance = `${confidence.toFixed(1)}% Confidence`;
            else if (confidence >= 50) significance = `${confidence.toFixed(1)}% Confidence`;
            else significance = `${confidence.toFixed(1)}% Confidence`;

            // Store results for both variants
            if (!results[variant1.variant] || results[variant1.variant].confidence < confidence) {
              results[variant1.variant] = {
                significance: `${significance} vs ${variant2.variant}`,
                confidence,
                pValue,
              };
            }
            if (!results[variant2.variant] || results[variant2.variant].confidence < confidence) {
              results[variant2.variant] = {
                significance: `${significance} vs ${variant1.variant}`,
                confidence,
                pValue,
              };
            }
          } else {
            // Numeric outcomes - use t-test approximation
            const pooledStdDev = Math.sqrt((Math.pow(value1, 2) + Math.pow(value2, 2)) / 2);
            const standardError = pooledStdDev * Math.sqrt(1 / count1 + 1 / count2);
            const zScore = Math.abs(value2 - value1) / standardError;

            const pValue = (2 * Math.exp((-zScore * zScore) / 2)) / Math.sqrt(2 * Math.PI);
            const confidence = Math.max(0, (1 - pValue) * 100);

            let significance = "Not Significant";
            if (confidence >= 99) significance = `${confidence.toFixed(1)}% Confidence`;
            else if (confidence >= 95) significance = `${confidence.toFixed(1)}% Confidence`;
            else if (confidence >= 90) significance = `${confidence.toFixed(1)}% Confidence`;
            else if (confidence >= 80) significance = `${confidence.toFixed(1)}% Confidence`;
            else if (confidence >= 70) significance = `${confidence.toFixed(1)}% Confidence`;
            else if (confidence >= 60) significance = `${confidence.toFixed(1)}% Confidence`;
            else if (confidence >= 50) significance = `${confidence.toFixed(1)}% Confidence`;
            else significance = `${confidence.toFixed(1)}% Confidence`;

            if (!results[variant1.variant] || results[variant1.variant].confidence < confidence) {
              results[variant1.variant] = {
                significance: `${significance} vs ${variant2.variant}`,
                confidence,
                pValue,
              };
            }
            if (!results[variant2.variant] || results[variant2.variant].confidence < confidence) {
              results[variant2.variant] = {
                significance: `${significance} vs ${variant1.variant}`,
                confidence,
                pValue,
              };
            }
          }
        } catch (error) {
          // Fallback to simple z-test
          if (metric === "successRateValue") {
            const se1 = Math.sqrt((value1 * (100 - value1)) / count1);
            const se2 = Math.sqrt((value2 * (100 - value2)) / count2);
            const pooledSE = Math.sqrt(se1 * se1 + se2 * se2);

            if (pooledSE > 0 && !isNaN(pooledSE)) {
              const zScore = Math.abs(value2 - value1) / pooledSE;
              const pValue = (2 * Math.exp((-zScore * zScore) / 2)) / Math.sqrt(2 * Math.PI);
              const confidence = Math.max(0, (1 - pValue) * 100);

              let significance = "Not Significant";
              if (confidence >= 99) significance = `${confidence.toFixed(1)}% Confidence`;
              else if (confidence >= 95) significance = `${confidence.toFixed(1)}% Confidence`;
              else if (confidence >= 90) significance = `${confidence.toFixed(1)}% Confidence`;
              else if (confidence >= 80) significance = `${confidence.toFixed(1)}% Confidence`;
              else if (confidence >= 70) significance = `${confidence.toFixed(1)}% Confidence`;
              else if (confidence >= 60) significance = `${confidence.toFixed(1)}% Confidence`;
              else if (confidence >= 50) significance = `${confidence.toFixed(1)}% Confidence`;
              else significance = `${confidence.toFixed(1)}% Confidence`;

              if (!results[variant1.variant] || results[variant1.variant].confidence < confidence) {
                results[variant1.variant] = {
                  significance: `${significance} vs ${variant2.variant}`,
                  confidence,
                  pValue,
                };
              }
              if (!results[variant2.variant] || results[variant2.variant].confidence < confidence) {
                results[variant2.variant] = {
                  significance: `${significance} vs ${variant1.variant}`,
                  confidence,
                  pValue,
                };
              }
            }
          } else {
            // For numeric outcomes, use simple comparison
            const pooledStdDev = Math.sqrt((Math.pow(value1, 2) + Math.pow(value2, 2)) / 2);
            if (pooledStdDev > 0) {
              const standardError = pooledStdDev * Math.sqrt(1 / count1 + 1 / count2);
              const zScore = Math.abs(value2 - value1) / standardError;

              const pValue = (2 * Math.exp((-zScore * zScore) / 2)) / Math.sqrt(2 * Math.PI);
              const confidence = Math.max(0, (1 - pValue) * 100);

              let significance = "Not Significant";
              if (confidence >= 99) significance = `${confidence.toFixed(1)}% Confidence`;
              else if (confidence >= 95) significance = `${confidence.toFixed(1)}% Confidence`;
              else if (confidence >= 90) significance = `${confidence.toFixed(1)}% Confidence`;
              else if (confidence >= 80) significance = `${confidence.toFixed(1)}% Confidence`;
              else if (confidence >= 70) significance = `${confidence.toFixed(1)}% Confidence`;
              else if (confidence >= 60) significance = `${confidence.toFixed(1)}% Confidence`;
              else if (confidence >= 50) significance = `${confidence.toFixed(1)}% Confidence`;
              else significance = `${confidence.toFixed(1)}% Confidence`;

              if (!results[variant1.variant] || results[variant1.variant].confidence < confidence) {
                results[variant1.variant] = {
                  significance: `${significance} vs ${variant2.variant}`,
                  confidence,
                  pValue,
                };
              }
              if (!results[variant2.variant] || results[variant2.variant].confidence < confidence) {
                results[variant2.variant] = {
                  significance: `${significance} vs ${variant1.variant}`,
                  confidence,
                  pValue,
                };
              }
            }
          }
        }
      }
    }
  }
  return results;
};
